import React from 'react';

import {observer} from 'mobx-react';
import {GameTitleBox} from '../../boxes/game-title/game-title.box';
import classNames from 'classnames';
import {ENABLE_HOVER_ANIMATION, TWO_LINES_TITLE} from '../../../css-constants';
import {OrbitComponent} from '../../../../base-components/orbit-component/orbit-component';

import {GameInfo} from '../../../../../services/games/game-info';
import {MinMaxBetAmountBox} from '../../boxes/min-max-bet-amount/min-max-bet-amount.box';
import {InfoPageButtonComponent} from '../info-page-button/info-page-button.component';
import {GameButtonsBox} from '../../boxes/game-buttons/game-buttons.box';
import {PlayDemoButtonComponent} from '../play-demo-button/play-demo-button.component';
import {GameDetailsContainerBox} from '../../boxes/game-details-container/game-details-container.box';
import {JackpotAmountBox} from '../../boxes/jackpot-amount/jackpot-amount.box';
import {GameJackpotComponent} from '../../../../game-jackpot/game-jackpot.component';
import {GameInfoContainerBox} from '../../boxes/game-info-container/game-info-container.box';
import {CyClass} from "../../../../../../common/cy-classes";
import { AmountContainerBox } from '../../boxes/amount-container/amount-container.box';
import { TwoRowsGameTitleContainerBox } from '../../boxes/two-rows-game-title-container/two-rows-game-title-container.box';

export interface GameDetailsContainerComponentProps {
    gameInfo: GameInfo;
    disableGameInfoHover: boolean;
    onOpenAnonymousGame: () => void;
    onOpenInfoPage: () => void;
    squareGameIcon?: boolean;
    hasTwoLinesTitle: boolean;
}

export const GameDetailsContainerComponent = observer(
    class GameDetailsContainerComponent extends OrbitComponent<GameDetailsContainerComponentProps> {

        private get _allowHoverDetails(): boolean {

            if (this.props.disableGameInfoHover || this.services.device.isMobile || !this.props.gameInfo.allowHoverDetails) {
                return false;
            }

            return Boolean(this.props.gameInfo.hasInfoPage || this.props.gameInfo.allowDemoButtonInSingleGame);
        }


        private _renderGameButtons() {
            if (!this._allowHoverDetails) {
                return null;
            }

            return (

                <GameButtonsBox>
                    <PlayDemoButtonComponent enabled={this.props.gameInfo.allowDemoButtonInSingleGame} onOpenAnonymousGame={this.props.onOpenAnonymousGame}/>
                    <InfoPageButtonComponent enabled={this.props.gameInfo.hasInfoPage} onOpenInfoPage={this.props.onOpenInfoPage}/>
                </GameButtonsBox>
            );
        }

        private _renderAmount() {
            if (this.props.gameInfo.hasJackpot) {
                return this._renderJackpot();
            }

            const formattedMinBaxBets = this.services.currency.formatMinMaxBet(this.props.gameInfo.minBet, this.props.gameInfo.maxBet);
            const hasMinMaxBets = this.props.gameInfo.allowMinMaxBet && formattedMinBaxBets;
            if (hasMinMaxBets) {
                return this._renderMinBaxBet();
            }

            return null;
        }

        private _hasDetails() {
            return this.props.gameInfo.allowMinMaxBet || this.props.gameInfo.hasJackpot;
        }

        private _renderJackpot() {
            return (
              <JackpotAmountBox className='cy-game-jackpot-amount'>
                  <GameJackpotComponent gameType={this.props.gameInfo.gameType}/>
              </JackpotAmountBox>
            );
        }

        private _renderMinBaxBet() {
            const formattedMinBaxBets = this.services.currency.formatMinMaxBet(this.props.gameInfo.minBet, this.props.gameInfo.maxBet);
            return (
                <MinMaxBetAmountBox className="cy-game-min-max-bet">
                    {formattedMinBaxBets}
                </MinMaxBetAmountBox>
            );
        }

        private _renderTitle() {
            const hasDetails = this._hasDetails();
            if (this.props.hasTwoLinesTitle) {
                return <TwoRowsGameTitleContainerBox className={classNames({"has-details": hasDetails})} hasDetails={hasDetails}>
                    <GameTitleBox
                        className={classNames(CyClass('cy-game-title'), {[TWO_LINES_TITLE]: true})}
                        hasTwoLinesTitle={true}
                    >
                        {this.props.gameInfo.title}
                    </GameTitleBox>
                </TwoRowsGameTitleContainerBox>;
            } else {
                return <GameTitleBox
                    className={classNames(CyClass('cy-game-title'), {[TWO_LINES_TITLE]: !hasDetails, "has-details": hasDetails})}
                    hasTwoLinesTitle={false}
                >
                    {this.props.gameInfo.title}
                </GameTitleBox>;
            }
        }

        private _renderGameInfoSection() {
            return (
                <GameInfoContainerBox className={classNames('cy-game-info-section', {[ENABLE_HOVER_ANIMATION]: this._allowHoverDetails})}>
                    {this._renderTitle()}
                    {this.props.hasTwoLinesTitle || this._hasDetails()?
                        <AmountContainerBox className={classNames({"has-two-lines-layout": this.props.hasTwoLinesTitle, "has-jp": this.props.gameInfo.hasJackpot, "has-min-max": this.props.gameInfo.allowMinMaxBet})}>{this._renderAmount()}</AmountContainerBox>
                    : null}
                </GameInfoContainerBox>
            );
        }

        render() {
            return (
                <GameDetailsContainerBox className="cy-game-details-container"
                                         squareGameIcon={this.props.squareGameIcon}>
                    {this._renderGameInfoSection()}
                    {this._renderGameButtons()}
                </GameDetailsContainerBox>
            );
        }
    }
);
